"use client";

/**
 * The teams-gallery design axes as ONE context. Every axis the tweaks panel can
 * change lives here — the deep-tree ones (field shell) that no
 * host could thread down to their renderers, AND the deck/expanded ones that
 * used to be `Cteamsgallery` state.
 *
 * The owner is whoever mounts the panel (the landing's `TeamsGalleryTweaksHost`),
 * so a selection reaches every surface that host wraps: the deck, the standalone
 * team overlay, and the preflight — including the surfaces opened WITHOUT the
 * deck. `createPortal` children stay inside the React tree, so the portaled
 * overlays see the provider too.
 *
 * The context default is the production design, so a host with no provider (the
 * session screen) renders exactly what `GALLERY_PRODUCTION_VARIANTS` describes.
 * No surface pins its own values: pinning is what made the panel's selections
 * stop at the deck's edge.
 */

import { createContext } from "react";
import {
  DEFAULT_ENTER_VARIANT,
  DEFAULT_FIELD_INLAY_VARIANT,
  DEFAULT_FIELD_SHELL_VARIANT,
  DEFAULT_MEMBERS_COLUMN_WIDTH_VARIANT,
  DEFAULT_MODAL_INLINE_INSET_VARIANT,
  DEFAULT_RAIL_BACK_VARIANT,
  DEFAULT_RAIL_EDIT_CONTEXT_VARIANT,
  DEFAULT_RAIL_EXPANSION_VARIANT,
  DEFAULT_RAIL_FOLD_SURFACE_VARIANT,
  DEFAULT_RAIL_PULSE_VARIANT,
  DEFAULT_RAIL_TOGGLE_REVEAL_VARIANT,
  GALLERY_PRODUCTION_VARIANTS,
  type EnterVariant,
  type FieldInlayVariant,
  type FieldShellVariant,
  type MembersColumnWidthVariant,
  type ModalInlineInsetVariant,
  type RailBackVariant,
  type RailEditContextVariant,
  type RailExpansionVariant,
  type RailFoldSurfaceVariant,
  type RailPulseVariant,
  type RailToggleRevealVariant,
  type TeamCardVariant,
} from "./gallery.constants";

export interface TeamsGalleryTweakBundle {
  /** How each team field is enclosed — see `FieldShellVariant`. The fields panel
   *  renders inside Edit's content tree and inside the preflight's left column,
   *  so the axis rides the context instead of being threaded through both hosts
   *  (that threading is exactly what the retired `fieldCardVariant` prop did). */
  teamFieldShellVariant: FieldShellVariant;
  /** What a written field's WELL is made of — see `FieldInlayVariant`. It rides
   *  the context for the same reason the shell does: the inputs are two
   *  components below every one of the three hosts that mount the fields panel,
   *  and none of them should learn the well has treatments. */
  fieldInlayVariant: FieldInlayVariant;
  /** The shared Start/Save rail's leading back affordance. */
  railBackVariant: RailBackVariant;
  railExpansionVariant: RailExpansionVariant;
  railPulseVariant: RailPulseVariant;
  /** How the team-edit action line's middle names what is being edited — see
   *  `RailEditContextVariant`. It rides the context like the pulse does: the
   *  reading renders two components below both team-edit mounts, and neither
   *  mount should learn the middle has treatments. */
  railEditContextVariant: RailEditContextVariant;
  railToggleRevealVariant: RailToggleRevealVariant;
  railFoldSurfaceVariant: RailFoldSurfaceVariant;
  modalInlineInsetVariant: ModalInlineInsetVariant;
  membersColumnWidthVariant: MembersColumnWidthVariant;
  cardVariant: TeamCardVariant;
  enterVariant: EnterVariant;
}

/** The production design. Every axis `GALLERY_PRODUCTION_VARIANTS` already owns
 *  is read from it, so the locked bundle stays the single source of that truth
 *  and a provider-less host cannot drift from it. */
export const GALLERY_DEFAULT_TWEAK_BUNDLE: TeamsGalleryTweakBundle = {
  teamFieldShellVariant: DEFAULT_FIELD_SHELL_VARIANT,
  fieldInlayVariant: DEFAULT_FIELD_INLAY_VARIANT,
  railBackVariant: DEFAULT_RAIL_BACK_VARIANT,
  railExpansionVariant: DEFAULT_RAIL_EXPANSION_VARIANT,
  railPulseVariant: DEFAULT_RAIL_PULSE_VARIANT,
  railEditContextVariant: DEFAULT_RAIL_EDIT_CONTEXT_VARIANT,
  railToggleRevealVariant: DEFAULT_RAIL_TOGGLE_REVEAL_VARIANT,
  railFoldSurfaceVariant: DEFAULT_RAIL_FOLD_SURFACE_VARIANT,
  modalInlineInsetVariant: DEFAULT_MODAL_INLINE_INSET_VARIANT,
  membersColumnWidthVariant: DEFAULT_MEMBERS_COLUMN_WIDTH_VARIANT,
  cardVariant: GALLERY_PRODUCTION_VARIANTS.cardVariant,
  enterVariant: DEFAULT_ENTER_VARIANT,
};

export const TeamsGalleryTweaksContext = createContext<TeamsGalleryTweakBundle>(
  GALLERY_DEFAULT_TWEAK_BUNDLE,
);
